<template>
  <div>
    <div class="flex gap-2">
      <div>
        <slot
          name="main-price"
          :label="label"
          :total-price-to-display="totalPriceToDisplay"
          :loading-pricings="loadingPricings"
          :tooltip-text="tooltipText"
          :type="type"
          :is-giftcard="isGiftcard"
          :hide-tooltip="hideTooltip"
        >
          <div class="main-price">
            <span v-if="label">{{ label }}</span>

            <!-- loader -->
            <span
              class="h-[30.8px]"
              :class="{
                'w-[110px] bg-gray-200 animate-pulse':
                  loadingPricings || !totalPriceToDisplay,
              }"
            >
              <template v-if="!loadingPricings && totalPriceToDisplay">
                <span v-if="totalPriceToDisplay">{{
                  totalPriceToDisplay
                }}</span>
              </template>
            </span>

            <Tooltip
              v-if="!isGiftcard && !hideTooltip"
              :key="tooltipText"
              :tooltip-text="tooltipText"
              length="large"
              position="down"
              type="span"
            >
              <div class="flex">
                <Badge
                  v-if="type === 'PRIVATE'"
                  class="bg-teal-50 text-primary m-0"
                >
                  {{ t('product.show.checkout.buyBoxPrivateBadge') }}
                </Badge>
                <InfoCircleFill v-else class="text-gray-500" />
              </div>
            </Tooltip>
          </div>
        </slot>

        <div class="special-price-label">
          <template v-if="!isGiftcard">
            <span v-if="specialPriceLabel">{{ specialPriceLabel }}</span>
            <span
              v-else-if="hasTiers && fromPricePerPerson"
              class="flex items-center gap-1 text-teal-600"
            >
              <IconTags class="w-4 h-4" />
              {{ fromPricePerPerson }}
            </span>
            <span v-else v-html="formattedPriceSubtitle"></span>
          </template>
        </div>
      </div>
      <span
        v-if="eventGetters.isBoxEvent(kftEvent) && !isGiftcard"
        class="text-gray-400 text-xs pt-2"
      >
        {{ t('product.show.checkout.shipping.excludingShipping') }}
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type {
  EventDate,
  EventDateType,
  EventDescription,
  Pricings,
} from '~/composables/types/event'
import InfoCircleFill from '~/assets/icons/info-circle-fill.svg?component'
import IconTags from '~/assets/icons/tags.svg?component'
import { useEventPricingsGetters } from '~/composables/useEventPricingsGetters'
import { formatPriceAmount, formatMoney } from '~/utils/currencyHelpers'
import { convertLocaleIntoBCP47 } from '~/utils/localeHelpers'
import { eventGetters } from '~/utils/eventGetters'
import { calculatePromotionDiscount } from '~/utils/pricingGetters'

const props = defineProps({
  label: {
    type: String,
    required: false,
    default: '',
  },
  specialPriceLabel: {
    type: String,
    required: false,
    default: '',
  },
  selectedTime: {
    type: Object as PropType<EventDate | undefined>,
    required: false,
    default: undefined,
  },
  kftEvent: {
    type: Object as PropType<EventDescription | undefined>,
    required: true,
  },
  type: {
    type: String as PropType<EventDateType>,
    required: true,
    default: 'PUBLIC',
  },
  hideTooltip: {
    type: Boolean,
    default: false,
  },
  selectedTicketQuantity: {
    type: Number as PropType<number | null>,
    default: 1,
  },
  hasAvailableDates: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  isGiftcard: {
    type: Boolean,
    default: false,
  },
  loadingPricings: {
    type: Boolean,
    default: false,
  },
  showFromPrefix: {
    type: Boolean,
    default: false,
  },
})

const { t, locale } = useI18n()

const pricings = (inject('pricingsData') as Record<string, Pricings>) ?? {}
const dateId = computed(() => {
  return toRef(props, 'selectedTime')?.value?.id
})

const { totalPrice, hasSale, pricingData } = useEventPricingsGetters(
  ref(pricings),
  {
    dateId,
    quantity: computed(() => props.selectedTicketQuantity ?? 1),
    event: toRef(props, 'kftEvent'),
  }
)

// Check if event is a kits or giftcard event
const isKitsOrGiftcardEvent = computed(() => {
  return (
    eventGetters.isBoxEvent(props.kftEvent) ||
    eventGetters.isGiftcardEvent(props.kftEvent) ||
    props.isGiftcard
  )
})

// Use totalPrice directly - no debounce needed since fetchPricings is already debounced
// Add fallback price for kits and giftcard events when pricing data is not available
const totalPriceToDisplay = computed(() => {
  // If pricing data is available, use it
  if (totalPrice.value) {
    return totalPrice.value
  }

  // Fallback for kits and giftcard events when no pricing data is available
  if (isKitsOrGiftcardEvent.value && !props.loadingPricings) {
    const priceAmount = props.isGiftcard
      ? eventGetters.getRetailPriceAmount(props.kftEvent)
      : eventGetters.getDefaultPriceAmount(props.kftEvent)

    if (priceAmount) {
      const quantity = props.selectedTicketQuantity ?? 1
      const totalInCents = Number(priceAmount) * quantity
      const currency =
        eventGetters.getDefaultPriceCurrency(props.kftEvent) || 'EUR'
      return formatPriceAmount(
        totalInCents,
        convertLocaleIntoBCP47(locale.value),
        currency
      )
    }
  }

  return undefined
})

// Check if there's only one ticket category
const hasSingleCategory = computed(() => {
  const categories = props.kftEvent?.pricing?.data?.pricingCategories?.data
  return categories && categories.length === 1
})

// Check if tiers are available
const hasTiers = computed(() => {
  return eventGetters.hasTieredPricing(props.kftEvent)
})

// Get the lowest price per person from tiers (with discount applied if available)
const fromPricePerPerson = computed(() => {
  if (!hasTiers.value) return undefined

  const categories =
    props.kftEvent?.pricing?.data?.pricingCategories?.data || []

  const adultCategory = categories.find(
    (cat) => cat.ticket_category === 'ADULT'
  )

  const firstCategory = categories[0]

  const scales =
    adultCategory?.pricingScales?.data || firstCategory?.pricingScales?.data

  const lowestRetailPrice =
    scales?.[scales.length - 1]?.price?.data?.retail_price

  if (!lowestRetailPrice) {
    return undefined
  }

  // Apply promotion discount if available
  // retail_price is already in base currency (euros), not cents
  const promotion = props.kftEvent?.activePromotion?.data
  const originalPriceAmount = Number(lowestRetailPrice)
  const discountedAmount = calculatePromotionDiscount(
    originalPriceAmount,
    promotion
  )

  // Use discounted price if available, otherwise use original price
  const finalPriceAmount =
    discountedAmount !== undefined ? discountedAmount : originalPriceAmount

  const formatted = formatMoney(
    finalPriceAmount,
    convertLocaleIntoBCP47(locale.value),
    'EUR',
    2
  )
  return t('product.show.checkout.priceFrom') + ' ' + formatted
})

// Get original price per person from pricing breakdown when there's a sale
const originalPricePerPerson = computed(() => {
  const originalPrice =
    pricingData.value?.price_breakdown?.[0]?.original_price_per_person

  // Only show if original price is different from current price
  const currentPrice =
    pricingData.value?.price_breakdown?.[0]?.price_per_person?.formatted
  if (originalPrice?.formatted === currentPrice) {
    return undefined
  }

  return originalPrice?.formatted
})

// Get the price subtitle text with category if available
const priceSubtitleText = computed(() => {
  if (!eventGetters.getDefaultFormattedPrice(props.kftEvent)) {
    return ''
  }

  const trasnlationKey = eventGetters.isBoxEvent(props.kftEvent)
    ? 'product.show.checkout.priceSubtitleKit'
    : 'product.show.checkout.priceSubtitle'

  return t(trasnlationKey, {
    price: eventGetters.getDefaultFormattedPrice(props.kftEvent),
  })
})

// Format price subtitle with original price in parentheses when applicable
const formattedPriceSubtitle = computed(() => {
  const discountedPrice = eventGetters.getDefaultFormattedPrice(props.kftEvent)
  if (!discountedPrice) {
    return ''
  }

  const shouldShowOriginalPrice =
    originalPricePerPerson.value && hasSale.value && hasSingleCategory.value

  const trasnlationKey = eventGetters.isBoxEvent(props.kftEvent)
    ? 'product.show.checkout.priceSubtitleKit'
    : 'product.show.checkout.priceSubtitle'

  if (!shouldShowOriginalPrice) {
    // Return normal subtitle without HTML
    return priceSubtitleText.value
  }

  // Get the translation template by using a temporary placeholder
  // Vue i18n will replace {price} with our placeholder, then we replace it with formatted version
  const tempPlaceholder = '__PRICE_PLACEHOLDER__'
  const translationWithPlaceholder = t(trasnlationKey, {
    price: tempPlaceholder,
  })
  const originalPrice = originalPricePerPerson.value

  // Format: "Ab (€55.00) 35,75 € pro Ticket"
  return translationWithPlaceholder.replace(
    tempPlaceholder,
    `<span class="line-through text-gray-400">${originalPrice}</span> ${discountedPrice}`
  )
})

const tooltipText = computed(() =>
  t(
    props.isGiftcard
      ? 'product.show.checkout.info.giftcard'
      : props.type === 'PUBLIC'
        ? 'product.show.checkout.info.publicEvent'
        : 'product.show.checkout.info.privateEvent',
    {
      price: totalPrice.value,
    }
  )
)
</script>

<style lang="postcss" scoped>
.main-price {
  @apply text-h5 text-gray-900 font-semibold text-nowrap flex gap-2 items-center;
  @apply min-h-[28px];
}

.special-price {
  @apply text-xs text-gray-500 line-through gap-2;
}

.special-price-label {
  @apply text-label text-gray-500 leading-none flex gap-2;
}

.days-left-for-promo {
  @apply text-xs text-[#DB5826] gap-2;
}
</style>
